import { loadFaceApi } from "@/utils/lazyLibs";
import { createEscalationEngine } from "./escalationEngine";
import { episodeClosed, episodeOpened } from "../episodeSignals";
import { registerStrike, resetStrikes } from "./violationStrikeCounter";

let _modelsReady = false;
let _modelLoadPromise = null;
// Set by ensureModelsLoaded. Only read once _modelsReady is true, which implies it is loaded.
let faceapi = null;

const MIN_FACE_AREA_RATIO = 0.03;

// Configurable escalation timing -- see escalationEngine.js for the
// confirm/monitor workflow, and violationStrikeCounter.js for the shared
// warn/terminate decision (combined across Multiple-Person + Person-Switch).
const CONFIRMATION_WINDOW_MS = 2000; // initial check before logging the violation
const MONITOR_WINDOW_MS = 10000; // post-log monitoring before the popup fires
const VIOLATION_RATIO_THRESHOLD = 0.6;

const VIOLATION_TYPE = "MULTIPLE_FACE";

const engine = createEscalationEngine({
  confirmationWindowMs: CONFIRMATION_WINDOW_MS,
  monitorWindowMs: MONITOR_WINDOW_MS,
  violationRatioThreshold: VIOLATION_RATIO_THRESHOLD,
});

export const multiplePersonState = {};
export const multiplePersonGetters = {};
export const multiplePersonMutations = {};

// Self-initializing: does not depend on personSwitch.js's init sequence or
// its own `_modelsReady` flag. If personSwitch.js already loaded
// tinyFaceDetector this is a no-op; if it hasn't (or failed), multiple-person
// detection loads its own copy and keeps working regardless.
async function ensureModelsLoaded() {
  if (_modelsReady) return;
  if (faceapi && faceapi.nets.tinyFaceDetector.isLoaded) {
    _modelsReady = true;
    return;
  }
  if (!_modelLoadPromise) {
    _modelLoadPromise = loadFaceApi()
      .then((api) => {
        faceapi = api;
        if (faceapi.nets.tinyFaceDetector.isLoaded) return;
        return faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      })
      .then(() => {
        _modelsReady = true;
      })
      .catch((err) => {
        console.error("[Proctoring] Multiple-person model load failed:", err);
        _modelLoadPromise = null; // allow a retry on the next cycle
      });
  }
  await _modelLoadPromise;
}

export const multiplePersonActions = {
  async checkMultiplePersons({ dispatch }, { videoEl }) {
    if (!videoEl) return;

    await ensureModelsLoaded();
    if (!_modelsReady) return;

    try {
      const detections = await faceapi.detectAllFaces(
        videoEl,
        new faceapi.TinyFaceDetectorOptions()
      );

      const frameArea = (videoEl.videoWidth || 1) * (videoEl.videoHeight || 1);
      const significant = (detections || []).filter((d) => {
        const { width, height } = d.box;
        return (width * height) / frameArea >= MIN_FACE_AREA_RATIO;
      });
      const count = significant.length;

      if (count === 0) {
        // Inconclusive frame (occlusion, momentary "no face", lighting) --
        // leave any in-progress escalation completely untouched. This is
        // not evidence that a second person left; it is just a missed
        // sample. cameraProctoring.js's own "No face detected" violation
        // handles sustained face-absence separately.
        return;
      }

      const avgScore =
        significant.reduce((sum, d) => sum + (d.score || 0), 0) / count;

      const result = engine.evaluate(Date.now(), count > 1, {
        faceCount: count,
        avgConfidence: Number(avgScore.toFixed(3)),
      });

      switch (result.action) {
        case "confirmed":
          episodeOpened(VIOLATION_TYPE);
          // Initial 2s check held -- post the violation log now. No popup
          // yet: that only fires if the SAME episode is still violating
          // after the full 10s monitoring window below.
          dispatch("triggerViolation", {
            type: VIOLATION_TYPE,
            reason: "Multiple persons detected",
            meta: result.evidence,
          });
          break;

        case "escalate": {
          dispatch("signalEpisodeContinues", {
            type: VIOLATION_TYPE,
            reason: "Multiple persons detected",
          });
          // Still violating after the 10s monitor window -- this is the
          // popup/terminate decision point, shared across Multiple-Person +
          // Person-Switch: 2 combined strikes (any mix of types) warn, the
          // 3rd terminates.
          const strike = registerStrike("multiple-person", result.evidence);
          if (strike.action === "terminate") {
            dispatch("triggerViolation", {
              type: VIOLATION_TYPE,
              reason: "Multiple persons confirmed — session closed",
              isLog: true,
              meta: { ...result.evidence, strikeLog: strike.strikeLog },
            });
            window.dispatchEvent(new CustomEvent("multiple-person-alert"));
            dispatch("stopDetection");
          } else {
            window.dispatchEvent(
              new CustomEvent("multiple-person-warning", {
                detail: { strikeNumber: strike.strikeNumber },
              })
            );
          }
          break;
        }

        case "resolved":
          episodeClosed(VIOLATION_TYPE);
          window.dispatchEvent(new CustomEvent("multiple-person-resolved"));
          break;

        default:
          break;
      }
    } catch (err) {
      console.warn("[Proctoring] Multiple-person detection failed:", err);
    }
  },

  // Engine only, keeping the shared strike count.
  resetMultiplePersonEpisode() {
    engine.reset();
    episodeClosed(VIOLATION_TYPE);
  },

  cleanupMultiplePerson() {
    engine.reset();
    episodeClosed(VIOLATION_TYPE);
    resetStrikes();
  },
};
